import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { addXP } from '../services/firebase';
import { API_URL } from '../config';
import Confetti from 'react-confetti';
import './BossBattle.css';

function BossBattle() {
    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [score, setScore] = useState(0);
    const [hearts, setHearts] = useState(3);
    const [loading, setLoading] = useState(true);
    const [battleComplete, setBattleComplete] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [bossHealth, setBossHealth] = useState(100);
    const [playerHealth, setPlayerHealth] = useState(100);

    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, refreshUserData } = useAuth();
    const { getCachedBossBattle } = useDataCache();
    const { topic, completedModules } = location.state || {};

    useEffect(() => {
        if (!topic) {
            navigate('/home');
            return;
        }
        generateBossBattle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic]);

    const generateBossBattle = async () => {
        setLoading(true);
        try {
            // Try to get from cache first
            const cachedData = await getCachedBossBattle(topic);
            if (cachedData?.questions) {
                console.log('📦 Using cached boss battle questions');
                setQuestions(cachedData.questions);
                setLoading(false);
                return;
            }

            // Fall back to API call
            const response = await fetch(`${API_URL}/api/generate-boss-battle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic,
                    completedModules: completedModules || [],
                    questionCount: 10
                })
            });

            const data = await response.json();
            setQuestions(data.questions || []);
        } catch (error) {
            console.error('Error generating boss battle:', error);
            alert('Failed to generate boss battle. Please try again.');
            navigate('/learning-path', { state: { topic } });
        }
        setLoading(false);
    };

    const handleAnswerSelect = (answer) => {
        if (showFeedback) return;

        setSelectedAnswer(answer);
        setShowFeedback(true);

        const currentQ = questions[currentQuestion];
        const isCorrect = answer === currentQ.correctAnswer;

        if (isCorrect) {
            setScore(score + 1);
            // Damage the boss
            const damage = 10;
            setBossHealth(Math.max(0, bossHealth - damage));
        } else {
            // Player takes damage
            setHearts(hearts - 1);
            const damage = 10;
            setPlayerHealth(Math.max(0, playerHealth - damage));
        }
    };

    const handleNext = async () => {
        // Check for game over
        if (hearts <= 0) {
            // Defeat
            setBattleComplete(true);
            return;
        }

        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedAnswer(null);
            setShowFeedback(false);
        } else {
            // Battle complete!
            setBattleComplete(true);

            // Calculate XP reward
            const baseXP = 200;
            const bonusXP = score * 50;
            const healthBonus = Math.floor(playerHealth / 2);
            const totalXP = baseXP + bonusXP + healthBonus;

            // Award XP
            if (currentUser) {
                await addXP(currentUser.uid, totalXP, 'boss_battle');
                await refreshUserData();
            }

            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 5000);
        }
    };

    const getBossEmoji = () => {
        if (bossHealth > 66) return '👹';
        if (bossHealth > 33) return '😠';
        if (bossHealth > 0) return '😵';
        return '💀';
    };

    const getPlayerEmoji = () => {
        if (playerHealth > 66) return '🦸';
        if (playerHealth > 33) return '😰';
        if (playerHealth > 0) return '🤕';
        return '💀';
    };

    const getRankTitle = () => {
        const accuracy = (score / questions.length) * 100;
        if (accuracy === 100) return { title: 'LEGENDARY MASTER', color: '#FFD700', icon: '👑' };
        if (accuracy >= 90) return { title: 'EPIC CHAMPION', color: '#9333EA', icon: '⚔️' };
        if (accuracy >= 80) return { title: 'MASTER', color: '#3B82F6', icon: '🏆' };
        if (accuracy >= 70) return { title: 'WARRIOR', color: '#10B981', icon: '🛡️' };
        if (accuracy >= 60) return { title: 'FIGHTER', color: '#F59E0B', icon: '⚡' };
        return { title: 'APPRENTICE', color: '#EF4444', icon: '🔰' };
    };

    if (loading) {
        return (
            <div className="boss-battle-loading">
                <div className="boss-intro">
                    <h1 className="boss-title">⚔️ BOSS BATTLE ⚔️</h1>
                    <p className="boss-subtitle">Preparing your ultimate challenge...</p>
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    if (battleComplete) {
        const victory = hearts > 0;
        const rank = getRankTitle();
        const accuracy = Math.round((score / questions.length) * 100);
        const baseXP = 200;
        const bonusXP = score * 50;
        const healthBonus = Math.floor(playerHealth / 2);
        const totalXP = baseXP + bonusXP + healthBonus;

        return (
            <div className="boss-battle-complete">
                {showConfetti && victory && <Confetti recycle={false} numberOfPieces={500} />}

                <div className="battle-result">
                    {victory ? (
                        <>
                            <div className="victory-badge">🏆</div>
                            <h1 className="result-title">VICTORY!</h1>
                            <h2 className="rank-title" style={{ color: rank.color }}>
                                {rank.icon} {rank.title} {rank.icon}
                            </h2>
                        </>
                    ) : (
                        <>
                            <div className="defeat-badge">💀</div>
                            <h1 className="result-title">DEFEATED</h1>
                            <p className="defeat-message">The boss was too strong... Train harder and try again!</p>
                        </>
                    )}

                    <div className="battle-stats">
                        <div className="stat-box">
                            <div className="stat-icon">🎯</div>
                            <div className="stat-value">{accuracy}%</div>
                            <div className="stat-label">Accuracy</div>
                        </div>
                        <div className="stat-box">
                            <div className="stat-icon">✅</div>
                            <div className="stat-value">{score}/{questions.length}</div>
                            <div className="stat-label">Correct</div>
                        </div>
                        {victory && (
                            <>
                                <div className="stat-box">
                                    <div className="stat-icon">❤️</div>
                                    <div className="stat-value">{hearts}/3</div>
                                    <div className="stat-label">Hearts Left</div>
                                </div>
                                <div className="stat-box highlight">
                                    <div className="stat-icon">💎</div>
                                    <div className="stat-value">{totalXP}</div>
                                    <div className="stat-label">XP Earned</div>
                                </div>
                            </>
                        )}
                    </div>

                    {victory && (
                        <div className="xp-breakdown">
                            <h3>XP Breakdown:</h3>
                            <div className="xp-item">Base Reward: +{baseXP} XP</div>
                            <div className="xp-item">Correct Answers: +{bonusXP} XP ({score} × 50)</div>
                            <div className="xp-item">Health Bonus: +{healthBonus} XP</div>
                        </div>
                    )}

                    <div className="battle-actions">
                        <button
                            className="btn btn-primary btn-large"
                            onClick={() => navigate('/learning-path', { state: { topic } })}
                        >
                            Return to Learning Path
                        </button>
                        {!victory && (
                            <button
                                className="btn btn-danger btn-large"
                                onClick={() => window.location.reload()}
                            >
                                ⚔️ Retry Boss Battle
                            </button>
                        )}
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate('/progress')}
                        >
                            View Progress
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentQ = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
        <div className="boss-battle-container">
            {/* Boss Health Bar */}
            <div className="battle-arena">
                <div className="boss-section">
                    <div className="boss-character">{getBossEmoji()}</div>
                    <div className="health-container">
                        <div className="health-label">BOSS</div>
                        <div className="health-bar">
                            <div
                                className="health-fill boss-health"
                                style={{ width: `${bossHealth}%` }}
                            />
                        </div>
                        <div className="health-text">{bossHealth}/100</div>
                    </div>
                </div>

                <div className="vs-divider">⚔️</div>

                <div className="player-section">
                    <div className="player-character">{getPlayerEmoji()}</div>
                    <div className="health-container">
                        <div className="health-label">YOU</div>
                        <div className="health-bar">
                            <div
                                className="health-fill player-health"
                                style={{ width: `${playerHealth}%` }}
                            />
                        </div>
                        <div className="health-text">{playerHealth}/100</div>
                    </div>
                    <div className="hearts-display">
                        {[1, 2, 3].map(h => (
                            <span key={h} className={`heart ${h <= hearts ? 'active' : 'lost'}`}>
                                {h <= hearts ? '❤️' : '🖤'}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Progress */}
            <div className="battle-progress">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="progress-text">
                    Question {currentQuestion + 1} of {questions.length}
                </div>
            </div>

            {/* Question */}
            <div className="battle-question-card">
                <h2 className="question-text">{currentQ?.question}</h2>

                <div className="battle-options">
                    {currentQ?.options?.map((option, idx) => (
                        <button
                            key={idx}
                            className={`battle-option ${
                                showFeedback && option === currentQ.correctAnswer ? 'correct' :
                                showFeedback && option === selectedAnswer ? 'incorrect' : ''
                            }`}
                            onClick={() => handleAnswerSelect(option)}
                            disabled={showFeedback}
                        >
                            <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                            <span className="option-text">{option}</span>
                        </button>
                    ))}
                </div>

                {showFeedback && (
                    <div className={`battle-feedback ${selectedAnswer === currentQ.correctAnswer ? 'correct' : 'incorrect'}`}>
                        <div className="feedback-header">
                            {selectedAnswer === currentQ.correctAnswer ? (
                                <>
                                    <span className="feedback-icon">⚔️</span>
                                    <h3>CRITICAL HIT!</h3>
                                </>
                            ) : (
                                <>
                                    <span className="feedback-icon">💥</span>
                                    <h3>BOSS STRIKES BACK!</h3>
                                </>
                            )}
                        </div>
                        <p className="feedback-text">{currentQ.explanation}</p>
                    </div>
                )}
            </div>

            {showFeedback && (
                <button className="btn btn-primary btn-large battle-next-btn" onClick={handleNext}>
                    {currentQuestion < questions.length - 1 ? 'Next Question →' : '🏆 Complete Battle'}
                </button>
            )}

            {/* Score Display */}
            <div className="battle-score">
                <span className="score-label">Score:</span>
                <span className="score-value">{score}/{currentQuestion + (showFeedback ? 1 : 0)}</span>
            </div>
        </div>
    );
}

export default BossBattle;
